import { LicenseLine } from "@/components/LicenseLine";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { AffiliateCTA } from "./AffiliateCTA";
import { BrandLogo } from "./BrandLogo";
import { listCasinos } from "@/data/casinos";
import { paymentPath } from "@/lib/format";

export function Hero() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";

  const featured = listCasinos().sort((a, b) => b.overall_rating - a.overall_rating)[0];
  const totalCasinos = listCasinos().length;

  const quickFilters = [
    {
      label: t("label.swish"),
      to: paymentPath(prefix, lang, isSv ? "swish-casino" : "swish-casinos"),
    },
    { label: t("label.bankid"), to: paymentPath(prefix, lang, "bankid") },
    { label: t("label.trustly"), to: paymentPath(prefix, lang, "trustly") },
    { label: t("label.zimpler"), to: paymentPath(prefix, lang, "zimpler") },
    {
      label: isSv ? "Free spins" : "Free spins",
      to: `${prefix}/free-spins`,
    },
    {
      label: isSv ? "Utan Spelpaus" : "Without Spelpaus",
      to: `${prefix}/${isSv ? "casino-utan-spelpaus" : "casinos-without-spelpaus"}`,
    },
    {
      label: isSv ? "Svensk licens" : "Swedish license",
      to: `${prefix}/${isSv ? "casino-med-svensk-licens" : "casinos-with-swedish-license"}`,
    },
    { label: t("label.crypto"), to: paymentPath(prefix, lang, "bitcoin") },
  ];

  return (
    <section className="relative isolate overflow-hidden hero-mesh hero-grid text-white">
      {/* Decorative orbs */}
      <div className="pointer-events-none absolute -top-32 left-1/4 h-72 w-72 rounded-full bg-brand-500/30 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -bottom-40 right-1/4 h-72 w-72 rounded-full bg-gold-500/20 blur-3xl" aria-hidden />

      <div className="container-page relative grid items-center gap-10 py-8 md:py-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <span className="eyebrow-dark">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            {t("site.tagline")}
          </span>
          <h1 className="mt-5 text-4xl font-extrabold leading-[1.1] text-white drop-shadow-[0_2px_18px_rgba(8,15,55,0.6)] md:text-5xl lg:text-6xl">
            {isSv ? (
              <>
                Hitta rätt casino i Sverige —{" "}
                <span className="bg-gradient-to-r from-gold-300 to-gold-500 bg-clip-text text-transparent">
                  på en meny.
                </span>
              </>
            ) : (
              <>
                Find the right Swedish casino —{" "}
                <span className="bg-gradient-to-r from-gold-300 to-gold-500 bg-clip-text text-transparent">
                  on one menu.
                </span>
              </>
            )}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/85">
            {isSv
              ? "Vi jämför svenska casinon med licens, casinon utan svensk licens, bonusar, betalningsmetoder och spel — på svenska och engelska."
              : "We compare Swedish-licensed casinos, casinos without a Swedish license, bonuses, payment methods and games — in Swedish and English."}
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            {featured && <AffiliateCTA brandSlug={featured.slug} source="hero" placement="hero-card" label={`${t("cta.visitCasino")} — ${featured.name}`} />}
          </div>

          <div className="mt-8 grid max-w-xl grid-cols-3 gap-3">
            <Stat
              value={`${totalCasinos}`}
              label={isSv ? "Casinon jämförda" : "Casinos compared"}
            />
            <Stat
              value="2"
              label={isSv ? "Språk · SV / EN" : "Languages · SV / EN"}
            />
            <Stat
              value="2026"
              label={isSv ? "Uppdaterat" : "Updated"}
            />
          </div>

          <p className="mt-6 text-xs text-white/60">
            {t("affiliate.disclosure")}
          </p>
        </div>

        {/* Featured card on the right */}
        {featured && (
          <aside className="relative lg:col-span-5">
            <div className="absolute -inset-4 rounded-[28px] bg-gradient-to-br from-gold-400/20 via-transparent to-brand-400/30 blur-xl" aria-hidden />
            <div className="relative card-glass overflow-hidden rounded-3xl p-6 text-white shadow-glow">
              <div className="flex items-center justify-between">
                <span className="pill-gold">★ {t("label.priorityBrand")}</span>
                <span className="text-xs uppercase tracking-widest text-white/60">
                  #{1}
                </span>
              </div>
              <div className="mt-5 flex items-center gap-4">
                <BrandLogo
                  slug={featured.slug}
                  name={featured.name}
                  src={featured.logo_url}
                  size={72}
                />
                <div>
                  <p className="text-2xl font-bold">{featured.name}</p>
                  <p className="mt-0.5 text-sm text-white/70">
                    ★ {featured.overall_rating.toFixed(1)} / 5 ·{" "}
                    {featured.license_authority ?? "—"}
                  </p>
                </div>
              </div>
              <p className="mt-5 text-sm text-white/85">
                {isSv ? featured.bonus_summary_sv : featured.bonus_summary_en}
              </p>
              <LicenseLine casino={featured} />
              <ul className="mt-4 space-y-1.5 text-sm">
                {(isSv ? featured.pros_sv : featured.pros_en)
                  .slice(0, 3)
                  .map((p) => (
                    <li key={p} className="flex items-start gap-2 text-white/85">
                      <span className="mt-0.5 text-emerald-400">✓</span>
                      <span>{p}</span>
                    </li>
                  ))}
              </ul>
              <div className="mt-5 grid gap-2 sm:grid-cols-2">
                <a
                  href={`/go/${featured.slug}?source=hero&placement=hero-card&lang=${lang}`}
                  rel="sponsored noopener"
                  target="_blank"
                  className="btn-accent w-full"
                >
                  {t("cta.visitCasino")}
                </a>
                <Link
                  to={`${prefix}/${isSv ? "recensioner" : "reviews"}/${featured.slug}`}
                  className="btn-outline w-full"
                >
                  {t("cta.readReview")}
                </Link>
              </div>
              <p className="mt-3 text-[11px] text-white/55">
                18+ · {isSv ? "Spela ansvarsfullt" : "Gamble responsibly"} · Spelpaus.se
              </p>
            </div>
          </aside>
        )}
      </div>

      {/* Quick filter chips */}
      <div className="relative border-t border-white/10 bg-white/0">
        <div className="container-page flex items-center gap-2 overflow-x-auto py-3">
          <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-white/55">
            {isSv ? "Snabbfilter:" : "Quick filters:"}
          </span>
          {quickFilters.map((q) => (
            <Link
              key={q.to}
              to={q.to}
              className="inline-flex shrink-0 items-center rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-sm font-medium text-white no-underline hover:border-white/30 hover:bg-white/20"
            >
              {q.label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="stat-tile">
      <p className="text-xl font-extrabold leading-none text-white">{value}</p>
      <p className="mt-1 text-[11px] uppercase tracking-wider text-white/60">
        {label}
      </p>
    </div>
  );
}
